import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react'
import Map, { Marker, Popup, NavigationControl, FullscreenControl, ScaleControl, Source, Layer } from 'react-map-gl/maplibre'
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card'
import { Button } from '../ui/button'
import { Layers, MapPin, X, ThermometerSun, Leaf, Box } from 'lucide-react'
import { useTheme } from '../providers/ThemeProvider'
import { fieldBoundaries } from '../../data/fieldBoundaries'
import realTimeDataService from '../../services/realTimeDataService'
import 'maplibre-gl/dist/maplibre-gl.css'

// Crop Color Map matching Legend
const CROP_COLORS = {
  'Corn': '#F59E0B', // Gold
  'Soybeans': '#A4C639', // Green
  'Wheat': '#FDE68A', // Wheat
  'Vegetables': '#FF6347', // Tomato Red
  'Grassland/Pasture': '#90EE90'
};

// MapLibre GL styles
const LIGHT_STYLE = {
  version: 8,
  sources: {
    'raster-tiles': {
      type: 'raster',
      tiles: [
        'https://basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png'
      ],
      tileSize: 256,
      attribution: '&copy; OpenStreetMap &copy; CARTO'
    }
  },
  layers: [
    {
      id: 'simple-tiles',
      type: 'raster',
      source: 'raster-tiles',
      minzoom: 0,
      maxzoom: 22
    }
  ]
}
const DARK_STYLE = {
  version: 8,
  sources: {
    'raster-tiles': {
      type: 'raster',
      tiles: [
        'https://basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png'
      ],
      tileSize: 256,
      attribution: '&copy; OpenStreetMap &copy; CARTO'
    }
  },
  layers: [
    {
      id: 'simple-tiles',
      type: 'raster',
      source: 'raster-tiles',
      minzoom: 0,
      maxzoom: 22
    }
  ]
}

const SATELLITE_STYLE = {
  version: 8,
  sources: {
    'raster-tiles': {
      type: 'raster',
      tiles: [
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
      ],
      tileSize: 256,
      attribution: '&copy; Esri'
    }
  },
  layers: [
    {
      id: 'simple-tiles',
      type: 'raster',
      source: 'raster-tiles',
      minzoom: 0,
      maxzoom: 22
    }
  ]
}

export default function ModernMapView({ onViewChange }) {
  const { theme } = useTheme()
  const mapRef = useRef()
  const [viewState, setViewState] = useState({
    longitude: -77.2,
    latitude: 39.0,
    zoom: 10,
    pitch: 0,
    bearing: 0
  })
  const [showLayers, setShowLayers] = useState(false)
  const [selectedField, setSelectedField] = useState(null)
  const [showFieldBoundaries, setShowFieldBoundaries] = useState(true)
  const [basemap, setBasemap] = useState('default') // 'default' or 'satellite'
  const [is3DMode, setIs3DMode] = useState(false) // 3D Terrain & Extrusion Mode

  // Toggle 3D mode - adjusts pitch for 3D view
  const toggle3DMode = useCallback(() => {
    setIs3DMode(prev => {
      const newMode = !prev;
      setViewState(vs => ({
        ...vs,
        pitch: newMode ? 60 : 0,
        bearing: newMode ? -20 : 0
      }));
      return newMode;
    });
  }, []);

  // Data States
  const [weatherStations, setWeatherStations] = useState([
    { id: 1, name: "Station Alpha", lat: 39.01, lng: -77.22, temp: '--' },
    { id: 2, name: "Station Beta", lat: 38.95, lng: -77.15, temp: '--' },
    { id: 3, name: "Station Gamma", lat: 39.05, lng: -77.25, temp: '--' }
  ])
  const [fieldHealth, setFieldHealth] = useState({})

  // Layer Control State
  const [layers, setLayers] = useState({
    crops: true,
    soil: true,
    stations: true
  })

  // Navigation Logic
  const handleAnalyze = useCallback(() => {
    if (selectedField && onViewChange) {
      onViewChange('satellite', selectedField.id);
    }
  }, [selectedField, onViewChange]);

  // Fetch Data Effect
  useEffect(() => {
    const fetchData = async () => {
      // 1. Update Weather Stations with "Live" data
      const updatedStations = await Promise.all(weatherStations.map(async (station) => {
        try {
          const data = await realTimeDataService.getWeather(station.lat, station.lng);
          return { ...station, temp: data.current.temperature || '--' };
        } catch (e) {
          console.warn("Station update failed", e);
          return station;
        }
      }));
      setWeatherStations(updatedStations);

      // 2. Fetch Field Health (Mocking saturation for demo based on coordinates)
      // In a full app, this would query by field ID
      const healthMap = {};
      if (fieldBoundaries && fieldBoundaries.features) {
        for (const feature of fieldBoundaries.features) {
          // Simulate health check based on simplified logic or service call
          // Here we mock it for visual variance, later connect to cropHealth API
          const id = feature.properties.id;
          healthMap[id] = 50 + Math.floor(Math.random() * 50); // 50-100 random health
        }
      }
      setFieldHealth(healthMap);
    };

    fetchData();
    // Refresh every minute
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onMove = useCallback((evt) => {
    setViewState(evt.viewState)
  }, [])

  const mapStyle = basemap === 'satellite'
    ? SATELLITE_STYLE
    : (theme === 'dark' ? DARK_STYLE : LIGHT_STYLE)

  // Process field boundaries GeoJSON
  const geoJsonData = useMemo(() => {
    if (!fieldBoundaries || !fieldBoundaries.features) return null
    // Inject health data into properties for coloring
    const enrichedFeatures = fieldBoundaries.features.map(f => ({
      ...f,
      properties: {
        ...f.properties,
        health: fieldHealth[f.properties.id] ?? f.properties.health
      }
    }));
    return { ...fieldBoundaries, features: enrichedFeatures }
  }, [fieldHealth])

  // Extract markers from GeoJSON points
  const markers = useMemo(() => {
    if (!geoJsonData) return []
    return geoJsonData.features
      .filter(f => f.geometry.type === 'Point')
      .map(f => ({
        ...f,
        coordinates: f.geometry.coordinates
      }))
  }, [geoJsonData])

  const handleFeatureClick = useCallback((event) => {
    const feature = event.features?.[0]
    if (feature && feature.properties) {
      setSelectedField({
        ...feature.properties,
        coordinates: feature.geometry.type === 'Point'
          ? feature.geometry.coordinates
          : feature.geometry.coordinates[0]?.[0] // simplified for polygons
      })
    }
  }, [])

  return (
    <div className="relative h-full w-full">
      <Map
        {...viewState}
        onMove={onMove}
        ref={mapRef}
        style={{ width: '100%', height: '100%' }}
        mapStyle={mapStyle}
        attributionControl={false}
        interactiveLayerIds={showFieldBoundaries && geoJsonData ? ['field-boundaries-fill'] : []}
        onClick={handleFeatureClick}
        cursor={showFieldBoundaries ? 'pointer' : 'default'}
        terrain={is3DMode ? { source: 'terrain-elevation', exaggeration: 1.5 } : null}
      >
        <NavigationControl position="top-right" />
        <FullscreenControl position="top-right" />
        <ScaleControl position="bottom-left" />

        {/* 3D Terrain Elevation Source */}
        <Source
          id="terrain-elevation"
          type="raster-dem"
          tiles={['https://demotiles.maplibre.org/terrain-tiles/{z}/{x}/{y}.png']}
          tileSize={256}
        />

        {/* GeoJSON Source for field boundaries */}
        {showFieldBoundaries && geoJsonData && (
          <Source id="field-boundaries" type="geojson" data={geoJsonData}>
            {/* 3D Extrusion Layer - used when 3D mode is ON */}
            {is3DMode ? (
              <Layer
                id="field-boundaries-fill"
                type="fill-extrusion"
                paint={{
                  'fill-extrusion-color': layers.soil
                    ? [
                      'interpolate',
                      ['linear'],
                      ['get', 'health'],
                      0, '#ef4444',
                      50, '#eab308',
                      100, '#22c55e'
                    ]
                    : [
                      'match',
                      ['get', 'crop'],
                      'Corn', CROP_COLORS.Corn,
                      'Soybeans', CROP_COLORS.Soybeans,
                      'Wheat', CROP_COLORS.Wheat,
                      '#10B981' // Fallback
                    ],
                  'fill-extrusion-height': [
                    'interpolate',
                    ['linear'],
                    ['get', 'health'],
                    0, 50,  // Low health = low height (50m)
                    100, 500 // High health = tall height (500m)
                  ],
                  'fill-extrusion-base': 0,
                  'fill-extrusion-opacity': 0.85
                }}
              />
            ) : (
              /* 2D Flat Fill Layer - default */
              <Layer
                id="field-boundaries-fill"
                type="fill"
                paint={{
                  'fill-color': layers.soil
                    ? [
                      'interpolate',
                      ['linear'],
                      ['get', 'health'],
                      0, '#ef4444',
                      50, '#eab308',
                      100, '#22c55e'
                    ]
                    : [
                      'match',
                      ['get', 'crop'],
                      'Corn', CROP_COLORS.Corn,
                      'Soybeans', CROP_COLORS.Soybeans,
                      'Wheat', CROP_COLORS.Wheat,
                      '#10B981' // Fallback
                    ],
                  'fill-opacity': 0.6
                }}
              />
            )}
            <Layer
              id="field-boundaries-stroke"
              type="line"
              paint={{
                'line-color': theme === 'dark' ? '#ffffff' : '#333333',
                'line-width': 1.5,
                'line-opacity': 0.6
              }}
            />
          </Source>
        )}

        {/* Layer Control Button */}
        <div className="absolute top-4 left-4 z-10 flex gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setShowLayers(!showLayers)}
            className="bg-card/80 backdrop-blur-sm shadow-lg border-border"
          >
            <Layers className="h-4 w-4" />
          </Button>
          <Button
            variant={is3DMode ? "default" : "outline"}
            size="icon"
            onClick={toggle3DMode}
            className={`backdrop-blur-sm shadow-lg border-border ${is3DMode ? 'bg-primary text-primary-foreground' : 'bg-card/80'}`}
            title="Toggle 3D View"
          >
            <Box className="h-4 w-4" />
          </Button>
        </div>

        {/* Markers for point features */}
        {markers.map((marker, idx) => (
          <Marker
            key={idx}
            longitude={marker.coordinates[0]}
            latitude={marker.coordinates[1]}
            anchor="bottom"
          >
            <div className="cursor-pointer transition-transform hover:scale-110">
              <MapPin className="h-6 w-6 text-primary fill-primary drop-shadow-lg" />
            </div>
          </Marker>
        ))}

        {/* Weather Stations Layer */}
        {layers.stations && weatherStations.map((station) => (
          <Marker
            key={`station-${station.id}`}
            longitude={station.lng}
            latitude={station.lat}
            anchor="bottom"
          >
            <div className="flex flex-col items-center group">
              <div className="p-1.5 bg-blue-500 rounded-full shadow-lg group-hover:scale-110 transition-transform ring-2 ring-white">
                <ThermometerSun className="h-4 w-4 text-white" />
              </div>
              <span className="text-xs bg-black/80 text-white px-2 py-0.5 rounded-full mt-1 font-mono border border-white/20 shadow-md transform translate-y-1 opacity-100 transition-all">
                {station.temp}°C
              </span>
            </div>
          </Marker>
        ))}

        {/* Selected field popup */}
        {selectedField && selectedField.center && (
          <Popup
            longitude={selectedField.center[0]}
            latitude={selectedField.center[1]}
            anchor="bottom"
            onClose={() => setSelectedField(null)}
            closeButton={true}
            className="z-50"
          >
            <div className="p-3 min-w-[200px] text-foreground bg-popover rounded-lg shadow-lg">
              <div className="flex items-center gap-2 mb-2 border-b border-border pb-2">
                <Leaf className="w-4 h-4 text-primary" />
                <h3 className="font-semibold text-sm">{selectedField.name}</h3>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                <div>
                  <span className="text-muted-foreground block">Crop</span>
                  <span className="font-mono">{selectedField.crop || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground block">Health</span>
                  <span className={`font-bold ${selectedField.health > 80 ? 'text-green-600' : 'text-amber-500'}`}>
                    {selectedField.health}%
                  </span>
                </div>
                {selectedField.area && (
                  <div className="col-span-2">
                    <span className="text-muted-foreground block">Area</span>
                    <span>{selectedField.area} acres</span>
                  </div>
                )}
              </div>

              <Button
                size="sm"
                className="w-full text-xs h-8"
                onClick={handleAnalyze}
              >
                Analyze Field
              </Button>
            </div>
          </Popup>
        )}
      </Map>

      {/* Layer Panel */}
      {showLayers && (
        <Card className="absolute top-16 left-4 z-10 w-64 shadow-xl border bg-card/95 backdrop-blur-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Map Layers</CardTitle>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => setShowLayers(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Basemap</h4>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setBasemap('default')}
                  className={`text-xs p-2 rounded border transition-colors ${basemap === 'default' ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-accent'}`}
                >
                  Default
                </button>
                <button
                  onClick={() => setBasemap('satellite')}
                  className={`text-xs p-2 rounded border transition-colors ${basemap === 'satellite' ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-accent'}`}
                >
                  Satellite
                </button>
              </div>
            </div>

            <div>
              <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Overlays</h4>
              <div className="space-y-2">
                <label className="flex items-center space-x-2 cursor-pointer p-1 rounded hover:bg-accent transition-colors">
                  <input
                    type="checkbox"
                    checked={showFieldBoundaries}
                    onChange={(e) => setShowFieldBoundaries(e.target.checked)}
                    className="rounded border-primary text-primary focus:ring-primary"
                  />
                  <span className="text-sm">Field Boundaries</span>
                </label>

                <div className="pl-6 space-y-1">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="layerType"
                      checked={layers.crops && !layers.soil}
                      onChange={() => setLayers(l => ({ ...l, crops: true, soil: false }))}
                      className="rounded-full border-primary text-primary focus:ring-primary"
                    />
                    <span className="text-xs text-muted-foreground">Crop Types</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="layerType"
                      checked={layers.soil}
                      onChange={() => setLayers(l => ({ ...l, crops: false, soil: true }))}
                      className="rounded-full border-primary text-primary focus:ring-primary"
                    />
                    <span className="text-xs text-muted-foreground">Health Index</span>
                  </label>
                </div>

                <div className="border-t border-border my-2"></div>

                <label className="flex items-center space-x-2 cursor-pointer p-1 rounded hover:bg-accent transition-colors">
                  <input
                    type="checkbox"
                    checked={layers.stations}
                    onChange={(e) => setLayers(l => ({ ...l, stations: e.target.checked }))}
                    className="rounded border-primary text-primary focus:ring-primary"
                  />
                  <span className="text-sm">Weather Stations</span>
                </label>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
